/** One Processing report; its filters live in the URL so a report can be bookmarked or shared. */
import { reportPermissionsOf } from "@nexseed/shared";
import { PlantRequired } from "@/components/app/plant-required";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { PROCESSING_REPORTS } from "@/components/app/nav";
import { ListPageSkeleton } from "@/components/app/skeletons";
import { useAuth } from "@/features/auth/store";
import { useFacilities } from "@/features/auth/use-facilities";
import { GotProcessingReportPage } from "@/features/processing/reports/got-reports-page";
import { ProcessingReportsPage } from "@/features/processing/reports/processing-reports-page";
import { DATE_MODES } from "@/lib/dates";

const reportOf = (key: string) => PROCESSING_REPORTS.find((r) => r.key === key);

const search = z.object({
  dates: z.enum(DATE_MODES).optional().catch(undefined),
  from: z.string().optional(),
  to: z.string().optional(),
  cropId: z.coerce.number().int().positive().optional(),
  varietyId: z.coerce.number().int().positive().optional(),
  machineId: z.coerce.number().int().positive().optional(),
  // GOT reports.
  result: z.enum(["OK", "Fail", "BL", "RT"]).optional(),
  // Status reports (SeedTrac p327–332).
  mode: z.enum(["arrival", "processing"]).optional(),
  organiser: z.string().optional(),
  personId: z.coerce.number().int().positive().optional(),
  // Status reports' search bar (lot no., location, person, farmer).
  q: z.string().max(100).optional(),
});

export const Route = createFileRoute("/_app/processing/reports/$key")({
  validateSearch: search,
  // The report's own code opens it (user, 2026-10-16).
  beforeLoad: ({ params }) => {
    const { can } = useAuth.getState();
    if (!reportPermissionsOf("processing").some((c) => can(c)))
      throw redirect({ to: "/" });
    const report = reportOf(params.key);
    if (!report || !can(report.permission))
      throw redirect({ to: "/processing/reports" });
  },
  component: Page,
});

function Page() {
  const { key } = Route.useParams();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { selected, ready } = useFacilities();
  const report = reportOf(key);
  if (!report) return null;
  if (!ready) return <ListPageSkeleton />;
  if (!selected)
    return <PlantRequired title={report.label} what="The report" />;
  const props = {
    facilityId: selected.id,
    plantName: selected.name,
    search: s,
    onSearch: (next: Partial<typeof s>) =>
      void navigate({ search: (prev) => ({ ...prev, ...next }) }),
  };
  return report.key === "got-ok" || report.key === "periodical-got" ? (
    <GotProcessingReportPage
      key={report.key}
      reportKey={report.key}
      {...props}
    />
  ) : (
    <ProcessingReportsPage key={report.key} report={report.key} {...props} />
  );
}
